import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * Small text sizes for different viewport presets:
 * - xs: text-xs (tablet portrait)
 * - sm: text-sm (tablet landscape)
 * - default: text-base (iPad Pro, large tablet)
 * - lg: text-lg (touchscreen PC, 4K)
 */
export type SmallSize = "xs" | "sm" | "default" | "lg";

const smallVariants = cva("text-muted-foreground", {
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      default: "text-base",
      lg: "text-lg",
    },
    intent: {
      default: "text-muted-foreground",
      muted: "text-muted-foreground",
      error: "text-destructive",
      success: "text-green-600 dark:text-green-500",
      warning: "text-amber-600 dark:text-amber-500",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
    },
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
  },
  defaultVariants: {
    size: "sm",
    intent: "default",
    weight: "normal",
    align: "left",
  },
});

export interface SmallProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "children">,
    VariantProps<typeof smallVariants> {
  /**
   * The HTML element to render. Defaults to "p".
   */
  as?: "p" | "span" | "div";
  children: React.ReactNode;
}

/**
 * Small component for helper text, hints, and secondary information.
 * Use for captions, footnotes, and supplementary details.
 *
 * @example
 * ```tsx
 * <Small>Bitte bestätigen Sie Ihre Identität</Small>
 * <Small intent="error">Pflichtfeld</Small>
 * ```
 */
function Small({
  className,
  as: Component = "p",
  size,
  intent,
  weight,
  align,
  children,
  ...props
}: SmallProps) {
  return (
    <Component
      className={cn(smallVariants({ size, intent, weight, align }), className)}
      {...props}
    >
      {children}
    </Component>
  );
}

Small.displayName = "Small";

export { Small };
